/**
 * `window.__marg.creature` (creatures §D3; GDD §4.15): DNA in/out, fields, «Наугад», ghost DNA, build stats. Works on
 * the "current creature" set by the creature lab (and by «Облик» in wave 2).
 */
import { DNA_FIELDS, buildDNA, dnaCodec, fieldsOf, ghostDNA, kunst, randomizeDNA, sliderValue } from './dna';
import { buildCreature } from './mesh';
import { labCreature, setLabCreature } from './screens/labState';
import type { KunstId } from '../shared';

export interface FieldInfo { name: string; bits: number; kind: string; group: string; value: number; real?: number }

export function creatureDebugApi(): Record<string, unknown> {
  const cur = () => labCreature();
  const rebuild = (code: string): string => {
    const d = dnaCodec.decode(code);
    if (!d) throw new Error('bad DNA');
    const t0 = globalThis.performance?.now?.() ?? 0;
    const b = buildCreature(d);
    setLabCreature(d, b, (globalThis.performance?.now?.() ?? 0) - t0);
    return dnaCodec.encode(d);
  };
  return {
    fromDNA: (code: string) => rebuild(code),
    toDNA: () => { const c = cur(); return c ? dnaCodec.encode(c.dna) : null; },
    ghostDNA: (seed: string) => dnaCodec.encode(ghostDNA(seed)),
    kunst: (id: KunstId) => dnaCodec.encode(kunst(id).dna),
    listFields: (): FieldInfo[] => {
      const c = cur();
      const m = c ? fieldsOf(c.dna) : {};
      return DNA_FIELDS.map((f) => ({ name: f.name, bits: f.bits, kind: f.kind, group: f.group, value: m[f.name] ?? 0, real: f.kind === 'slider' ? sliderValue(f, m[f.name] ?? 0) : undefined }));
    },
    getField: (name: string) => { const c = cur(); return c ? fieldsOf(c.dna)[name] : undefined; },
    setField: (name: string, value: number) => {
      const c = cur(); if (!c) return null;
      const m = fieldsOf(c.dna); m[name] = value;
      return rebuild(dnaCodec.encode(buildDNA(m, c.dna.fields.nameMode ? c.dna.name : null)));
    },
    randomize: (opts: { scope?: 'section' | 'all'; derp?: number; seed?: number } = {}) => {
      const c = cur(); if (!c) return null;
      return rebuild(dnaCodec.encode(randomizeDNA(c.dna, String(opts.seed ?? 1), { derp: opts.derp })));
    },
    stats: () => {
      const c = cur();
      if (!c) return null;
      const b = c.built;
      return { bones: b.built.rig.bones.length, rigidChains: b.built.rig.rigidChains, tris: b.mesh.tris.render, hullTris: b.mesh.tris.hull, buildMs: c.buildMs,
        partRebuilds: c.partRebuilds, fullRebuilds: c.fullRebuilds, bodySpecies: b.spec.body.index, heightScale: b.spec.v.height };
    },
  };
}
